import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { LOGO_WHITE } from "./Logo";

const LS_KEY = "alta_invite_templates_v1";

const DEFAULTS = {
  chapeu: "CONVITE PESSOAL · 10 VAGAS · 2026",
  chamada: "CONVITE PARA",
  evento: "RETIRO ALTA AGENCY · SHAMBALA HOUSE",
  cta: "CONFIRME SUA PRESENÇA EM NOSSO SITE",
  codigoLabel: "{primeiro_nome}, seu código é",
  whatsapp:
    `✦ CASA ALTA 2026 ✦\n\n` +
    `{primeiro_nome}, você foi selecionada pela Alta Agency para o retiro criativo mais reservado do ano.\n\n` +
    `📅 04 a 06 de agosto de 2026\n` +
    `📍 Shambala House · Moema · São Paulo\n\n` +
    `⚠️ Este convite é PESSOAL E INTRANSFERÍVEL.\n\n` +
    `🔑 Sua chave de acesso pessoal:\n` +
    `{codigo}\n\n` +
    `👉 CONFIRME SUA PRESENÇA (obrigatório):\n` +
    `1. Acesse: {link}\n` +
    `2. Digite sua chave de acesso\n` +
    `3. Preencha o RSVP e assine o termo\n\n` +
    `Guarde bem este código — ele é sua entrada.`,
};

// Simple {token} interpolation
const interpolate = (s, ctx) =>
  (s || "").replace(/\{(nome|primeiro_nome|codigo|link)\}/g, (_, k) => ctx[k] ?? "");

/**
 * Invite card generator — renders a 1080x1350 personalized invite
 * that admin can download as PNG and/or share via WhatsApp.
 * Text is editable per-invite; templates can be saved as default (localStorage).
 */
export default function InviteCard({ code, label, onClose }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const [tpl, setTpl] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(LS_KEY) || "{}");
      return { ...DEFAULTS, ...stored };
    } catch {
      return { ...DEFAULTS };
    }
  });

  const guestUrl = `${window.location.origin}/convidado`;
  const guestName = (label || "").trim() || "Convidada Alta";
  const firstName = guestName.split(/\s+/)[0];
  const ctx = { nome: guestName, primeiro_nome: firstName, codigo: code, link: guestUrl };

  const setField = (k, v) => setTpl((t) => ({ ...t, [k]: v }));
  const resetDefaults = () => {
    if (window.confirm("Voltar todos os textos para o modelo padrão?")) {
      setTpl({ ...DEFAULTS });
      toast.success("Modelo padrão restaurado");
    }
  };
  const saveAsDefault = () => {
    localStorage.setItem(LS_KEY, JSON.stringify(tpl));
    toast.success("Modelo salvo como padrão");
  };

  // Prevent body scroll while modal open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const download = async () => {
    if (!ref.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, {
        cacheBust: true,
        pixelRatio: 2,
        canvasWidth: 1080,
        canvasHeight: 1350,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `casa-alta-convite-${code}.png`;
      a.click();
      toast.success("Convite baixado");
    } catch (err) {
      toast.error("Não foi possível gerar o PNG");
    } finally {
      setBusy(false);
    }
  };

  const shareWhatsapp = () => {
    const message = interpolate(tpl.whatsapp, ctx);
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(guestUrl);
    toast.success("Link copiado");
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="invite-modal"
    >
      <div
        className="max-w-[1100px] w-full bg-[#F9F8F6] my-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
              Gerar convite
            </div>
            <div className="mt-1 serif text-[22px] leading-none">{guestName}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="invite-modal-close"
            className="mono text-[11px] uppercase tracking-[0.24em] hover:text-[color:var(--alta-red)]"
          >
            Fechar ✕
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6">
          {/* Preview card — the actual visual that becomes the PNG */}
          <div className="md:col-span-8 flex justify-center bg-neutral-100 p-4">
            <div
              ref={ref}
              data-testid="invite-card"
              style={{
                width: 540,
                height: 675,
                background:
                  "radial-gradient(circle at 20% 15%, rgba(183,40,30,0.35) 0%, transparent 42%), radial-gradient(circle at 85% 85%, rgba(90,107,82,0.28) 0%, transparent 45%), linear-gradient(160deg, #0F0F0F 0%, #1A1A1A 55%, #050505 100%)",
                color: "#F9F8F6",
                position: "relative",
                fontFamily: "Manrope, sans-serif",
                overflow: "hidden",
              }}
            >
              {/* Alta ribbon top */}
              <div style={{ height: 4, background: "#B7281E", width: "100%" }} />

              {/* Faint Alta watermark bg */}
              <img
                src={LOGO_WHITE}
                alt=""
                crossOrigin="anonymous"
                style={{
                  position: "absolute",
                  right: -60,
                  bottom: -40,
                  height: 460,
                  width: "auto",
                  opacity: 0.05,
                  pointerEvents: "none",
                }}
              />

              {/* Content */}
              <div style={{ padding: "32px 34px 28px 34px", height: "calc(100% - 4px)", display: "flex", flexDirection: "column", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
                {/* Top row: logo + intransferível */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <img src={LOGO_WHITE} alt="Alta" style={{ height: 32, width: "auto" }} crossOrigin="anonymous" />
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      border: "1px solid #B7281E",
                      background: "#B7281E",
                      color: "#F9F8F6",
                      padding: "4px 10px",
                      fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 600,
                    }}>
                      <span style={{ width: 5, height: 5, borderRadius: 999, background: "#F9F8F6" }} />
                      <span>Intransferível</span>
                    </div>
                  </div>

                  <div style={{
                    marginTop: 18,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    border: "1px solid rgba(183,40,30,0.7)",
                    background: "rgba(183,40,30,0.18)",
                    padding: "5px 10px",
                    fontSize: 9,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: "#B7281E" }} />
                    <span>{interpolate(tpl.chapeu, ctx)}</span>
                  </div>

                  {/* Convidada em destaque */}
                  <div style={{ marginTop: 22, fontSize: 10, letterSpacing: "0.32em", textTransform: "uppercase", color: "#D14B3F" }}>
                    {interpolate(tpl.chamada, ctx)}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontFamily: "Fraunces, Cormorant Garamond, Georgia, serif",
                      fontWeight: 900,
                      fontSize: 54,
                      lineHeight: 0.95,
                      letterSpacing: "-0.02em",
                      color: "#F9F8F6",
                      wordBreak: "break-word",
                    }}
                  >
                    {guestName}
                  </div>

                  {/* Casa Alta — event title */}
                  <div style={{ marginTop: 22, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.65 }}>
                    {interpolate(tpl.evento, ctx)}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontFamily: "Fraunces, Cormorant Garamond, Georgia, serif",
                      fontWeight: 900,
                      fontSize: 62,
                      lineHeight: 0.9,
                      letterSpacing: "-0.035em",
                    }}
                  >
                    Casa <span style={{ color: "#D14B3F" }}>Alta</span>
                    <span style={{ color: "#B7281E" }}>.</span>
                  </div>
                </div>

                {/* Middle info strip */}
                <div style={{ display: "flex", gap: 24, borderTop: "1px solid rgba(249,248,246,0.28)", borderBottom: "1px solid rgba(249,248,246,0.28)", padding: "12px 0" }}>
                  <InfoStat tag="Data" v="04—06" s="agosto · 2026" />
                  <InfoStat tag="Local" v="Moema" s="São Paulo" />
                  <InfoStat tag="Formato" v="3 dias" s="fechado" />
                </div>

                {/* CTA + Code + QR */}
                <div>
                  <div style={{ fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "#D14B3F", marginBottom: 6 }}>
                    {interpolate(tpl.cta, ctx)}
                  </div>
                  <div style={{ display: "flex", gap: 16, alignItems: "flex-end", justifyContent: "space-between" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", opacity: 0.6 }}>
                        {interpolate(tpl.codigoLabel, ctx)}
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          fontFamily: "Fraunces, serif",
                          fontWeight: 900,
                          fontSize: 42,
                          letterSpacing: "0.08em",
                          color: "#F9F8F6",
                        }}
                      >
                        {code}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 9, opacity: 0.7, wordBreak: "break-all", maxWidth: 280 }}>
                        {guestUrl}
                      </div>
                    </div>
                    <div style={{ padding: 6, background: "#F9F8F6", flexShrink: 0 }}>
                      <QRCodeSVG value={guestUrl} size={90} bgColor="#F9F8F6" fgColor="#1A1A1A" level="M" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="md:col-span-4 flex flex-col gap-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">Código</div>
              <div className="mt-1 serif text-[36px] tracking-[0.08em] text-neutral-900">{code}</div>
            </div>

            <div>
              <div className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">Link do convite</div>
              <div className="mt-1 text-[12px] break-all text-neutral-700 border border-neutral-300 p-3 bg-neutral-50">
                {guestUrl}
              </div>
            </div>

            {/* Tab toggle */}
            <div className="flex border-b border-neutral-300" data-testid="invite-tabs">
              <button
                onClick={() => setEditing(false)}
                data-testid="invite-tab-actions"
                className={`flex-1 py-3 mono text-[10px] uppercase tracking-[0.28em] border-b-2 transition-colors ${
                  !editing ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-400"
                }`}
              >
                Ações
              </button>
              <button
                onClick={() => setEditing(true)}
                data-testid="invite-tab-edit"
                className={`flex-1 py-3 mono text-[10px] uppercase tracking-[0.28em] border-b-2 transition-colors ${
                  editing ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-400"
                }`}
              >
                Editar textos
              </button>
            </div>

            {!editing && (
              <>
                <button
                  onClick={download}
                  disabled={busy}
                  data-testid="invite-download"
                  className="btn-red-primary w-full"
                >
                  {busy ? "Gerando..." : "Baixar convite (PNG)"}
                </button>

                <button
                  onClick={shareWhatsapp}
                  data-testid="invite-whatsapp"
                  className="btn-sharp w-full"
                >
                  Compartilhar no WhatsApp
                </button>

                <button
                  onClick={copyLink}
                  data-testid="invite-copy-link"
                  className="btn-ghost w-full"
                >
                  Copiar link
                </button>

                <p className="mt-2 mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 leading-relaxed">
                  O PNG sai em 1080×1350 (formato stories/whatsapp). O botão de
                  WhatsApp abre o app com a mensagem já pronta.
                </p>
              </>
            )}

            {editing && (
              <div className="space-y-4" data-testid="invite-editor">
                <p className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 leading-relaxed">
                  Preview atualiza em tempo real. Use os marcadores:{" "}
                  <code className="text-[color:var(--alta-red)]">{"{nome}"}</code>,{" "}
                  <code className="text-[color:var(--alta-red)]">{"{primeiro_nome}"}</code>,{" "}
                  <code className="text-[color:var(--alta-red)]">{"{codigo}"}</code>,{" "}
                  <code className="text-[color:var(--alta-red)]">{"{link}"}</code>.
                </p>

                <EditorField
                  label="Chapéu (pill vermelho)"
                  testid="edit-chapeu"
                  value={tpl.chapeu}
                  onChange={(v) => setField("chapeu", v)}
                />
                <EditorField
                  label="Chamada acima do nome"
                  testid="edit-chamada"
                  value={tpl.chamada}
                  onChange={(v) => setField("chamada", v)}
                />
                <EditorField
                  label="Linha do evento"
                  testid="edit-evento"
                  value={tpl.evento}
                  onChange={(v) => setField("evento", v)}
                />
                <EditorField
                  label="CTA (antes do código)"
                  testid="edit-cta"
                  value={tpl.cta}
                  onChange={(v) => setField("cta", v)}
                />
                <EditorField
                  label="Rótulo do código"
                  testid="edit-codigo-label"
                  value={tpl.codigoLabel}
                  onChange={(v) => setField("codigoLabel", v)}
                />
                <EditorField
                  label="Mensagem do WhatsApp"
                  testid="edit-whatsapp"
                  multiline
                  rows={9}
                  value={tpl.whatsapp}
                  onChange={(v) => setField("whatsapp", v)}
                />

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={saveAsDefault}
                    data-testid="invite-save-default"
                    className="btn-sharp flex-1 !py-3 !px-4 !text-[10px]"
                  >
                    Salvar como padrão
                  </button>
                  <button
                    onClick={resetDefaults}
                    data-testid="invite-reset-default"
                    className="btn-ghost flex-1 !py-3 !px-4 !text-[10px]"
                  >
                    Resetar
                  </button>
                </div>

                <p className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 leading-relaxed">
                  As mudanças aqui valem apenas para este convite. Clique em
                  <em className="italic text-[color:var(--alta-red)]"> Salvar como padrão </em>
                  para reusar em todos os próximos convites.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoStat({ tag, v, s }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", color: "#D14B3F" }}>{tag}</div>
      <div style={{ marginTop: 4, fontFamily: "Cormorant Garamond, serif", fontSize: 22, lineHeight: 1, color: "#F9F8F6" }}>{v}</div>
      <div style={{ marginTop: 3, fontSize: 8, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.65 }}>{s}</div>
    </div>
  );
}

function EditorField({ label, value, onChange, testid, multiline, rows }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
        {label}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows || 4}
          className="mt-2 w-full border border-neutral-300 bg-white/70 p-3 text-[13px] leading-[1.55] text-neutral-900 focus:outline-none focus:border-[color:var(--alta-red)] font-[Manrope]"
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full border border-neutral-300 bg-white/70 p-3 text-[13px] text-neutral-900 focus:outline-none focus:border-[color:var(--alta-red)]"
        />
      )}
    </label>
  );
}
